import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReloadOutlined, SwapRightOutlined } from '@ant-design/icons';
import { Button, Empty } from 'antd';
import { SERVER_IP } from 'assets/Config';
// import ExcelDownload from 'components/excel-download';
import TableComponent from 'components/table-component';
import { convertToIndianRupees, formQueryStringFromObject } from 'helpers';
import { sendGetRequest } from 'redux/sagas/utils';
import { capitalizeFirstLetter, getDateFormat } from 'services/Utils';
import { SUPER_ADMIN_EMAILS } from 'constants/app-constants';
import './recent-payments.scss';

const RecentPayments = () => {
	const [dataSource, setDataSource] = useState({
		data: [],
		loading: true,
	});
	const globalRedux = useSelector((state) => state.globalRedux);
	const loginRedux = useSelector((state) => state.loginRedux);
	const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(loginRedux.email);

	const columns = useMemo(() => {
		if (isSuperAdmin) {
			return [
				{
					title: 'Gym Name',
					dataIndex: 'user',
					key: 'user',
					render: (value) => <span>{value?.gymName || '-'}</span>,
				},
				{
					title: 'Package',
					dataIndex: 'package',
					key: 'package',
					render: (value) => <span>{capitalizeFirstLetter(value) || ' - '}</span>,
				},
				{
					title: 'Paid On',
					dataIndex: 'paidOn',
					key: 'paidOn',
					render: (value) => <span>{getDateFormat(value) || ' - '}</span>,
				},
				{
					title: 'Payment',
					dataIndex: 'paymentMethod',
					key: 'paymentMethod',
					render: (value) => <span>{capitalizeFirstLetter(value) || ' - '}</span>,
				},
				{
					title: 'Amount',
					dataIndex: 'amount',
					key: 'amount',
					align: 'right',
					sorter: (a, b) => a.amount - b.amount,
					render: (text) => <span>{convertToIndianRupees(text)}</span>,
				},
			];
		} else {
			return [
				{
					title: 'Customer Name',
					dataIndex: 'customer',
					key: 'customer',
					render: (value) => <span>{value?.customerName || '-'}</span>,
				},
				{
					title: 'Service',
					dataIndex: 'service',
					key: 'service',
					render: (value) => <span>{value?.serviceName || ' - '}</span>,
				},
				{
					title: 'Membership',
					dataIndex: 'membership',
					key: 'membership',
					render: (value) => (
						<span
						style={{
							color: 'var(--primary-color, #4d7cfe)',
							fontWeight: 'bold',
							}}>
							{value?.membershipName || ' - '}
						</span>
					),
				},
				{
					title: 'Product',
					dataIndex: 'product',
					key: 'product',
					render: (value) => <span>{value?.productName || ' - '}</span>,
				},
				{
					title: 'Payment',
					dataIndex: 'paymentMethod',
					key: 'paymentMethod',
					render: (value) => <span>{capitalizeFirstLetter(value) || ' - '}</span>,
				},
				{
					title: 'Amount',
					dataIndex: 'amount',
					key: 'amount',
					align: 'right',
					sorter: (a, b) => a.amount - b.amount,
					render: (text) => <span>{convertToIndianRupees(text)}</span>,
				},
			];
		}
	}, [isSuperAdmin]);

	// const exportData = useMemo(() => {
	// 	const data = {
	// 		headers: columns?.map((col) => col?.title) || [],
	// 		data: [
	// 			...((dataSource?.data || []).map((stock, index) => ({
	// 				[columns[0]?.title]: stock?.date,
	// 				[columns[1]?.title]: parseFloat(stock?.totalAmount).toFixed(2),
	// 			})) || []),
	// 		],
	// 	};
	// 	return data;
	// }, [dataSource?.data, columns]);
	const getString = useCallback(
		() =>
			formQueryStringFromObject({
				branchId: globalRedux?.selectedBranch?.id,
				limit: 10,
				offset: 0,
				sortBy: 'updated_at',
				sortByOrder: 'DESC',
				isSuperAdmin,
			}),
		[globalRedux?.selectedBranch?.id, isSuperAdmin]
	);

	const getPayments = useCallback(async () => {
		try {
			setDataSource((dataSource) => ({
				...dataSource,
				loading: true,
			}));
			const string = getString();
			const {
				data: { data },
			} = await sendGetRequest(null, `${SERVER_IP}api/payments?${string}`);
			setDataSource({
				data: data,
				loading: false,
			});
		} catch (error) {
			setDataSource({
				data: [],
				loading: false,
			});
		}
	}, [getString]);

	useEffect(() => {
		if (isSuperAdmin) {
			getPayments();
		} else if (globalRedux?.selectedBranch?.id) {
			getPayments();
		}
	}, [getPayments, globalRedux?.selectedBranch?.id, isSuperAdmin]);

	const hasData = dataSource?.data && dataSource.data.length > 0;

	return (
		<div className="recent-payments-card">
			<div className="recent-payments-header">
				<div className="recent-payments-heading">
					<div className="recent-payments-heading__icon" aria-hidden="true">
						<SwapRightOutlined />
					</div>
					<div className="recent-payments-heading__copy">
						<span className="recent-payments-heading__eyebrow">Activity</span>
						<h3 className="recent-payments-heading__title">Recent Payments</h3>
					</div>
				</div>
				<div className="recent-payments-header__actions">
					{hasData ? <span className="recent-payments-count">{`${dataSource.data.length} entries`}</span> : null}
					<Button
						type="primary"
						icon={<ReloadOutlined />}
						loading={dataSource.loading}
						onClick={getPayments}
						size="small"
						className="reload-btn"
					/>
				</div>
			</div>
			{!dataSource.loading && !hasData ? (
				<div className="empty-state">
					<div className="empty-state__icon" aria-hidden="true">
						<SwapRightOutlined />
					</div>
					<Empty 
						description={<span className="empty-text">No recent payments yet</span>} 
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						imageStyle={{ opacity: 0.5 }}
					/>
				</div>
			) : (
				<div className="recent-payments-table">
					<TableComponent
						className="custom-table"
						dataSource={dataSource?.data || []}
						columns={columns}
						pagination={false}
						loading={dataSource.loading}
						style={{
							width: '100%',
						}}
						scroll={{ y: 344 }}
					/>
				</div>
			)}
		</div>
	);
};

export default RecentPayments;
